import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '@/app/human-design/chart/bodygraph/hd-content-language.service';
import { HdGateInfoService } from '@/app/human-design/chart/bodygraph/hd-gate-info.service';
import { HdBodygraphCenter, HdBodygraphConfig } from '@/app/human-design/chart/bodygraph/hd-bodygraph.models';

/** Lege ("referentie") bodygraph-config: geen activaties → alle centra open. */
const REFERENCE_BODYGRAPH_CONFIG: HdBodygraphConfig = { schemaVersion: 1, activations: [] };

/**
 * Pagina met de "Niet-Zelf Conditionering" cheat sheet, nagebouwd uit het Figma-
 * design "HUMAN DESIGN NIET-ZELF CONDITIONERING" (node 1138-783). Ligt in het
 * verlengde van de niet-zelf deck-pagina en gebruikt dezelfde kaartkleuren.
 *
 * Layout: het Figma-poster 1:1 — een grote centrale referentie-bodygraph met de
 * negen rijke conditionering-blokken er absoluut omheen/overheen gepositioneerd
 * op hun anatomische plek (posities opgemeten op het poster, zie de
 * `--<center>`-modifierklassen in de SCSS), plus de twee tekstblokken
 * (conditioneringsimpact & poort-terminologie) eronder. Op smalle schermen
 * valt het poster terug op een gestapelde lijst.
 *
 * De impact-rang, percentages, kleuren en focus-badges komen uit de bestaande
 * niet-zelf deck-data; de kaart-specifieke tekst (keynote, "Open"-vraag, fysiek)
 * uit de `human-design/conditioning`-scope. De twee tekstblokken zijn markdown
 * onder `assets/docs/human-design/conditioning/{lang}/`.
 */
@Component({
  selector: 'hd-conditioning-cheat-sheet-page',
  standalone: false,
  templateUrl: './hd-conditioning-cheat-sheet-page.component.html',
  styleUrl: './hd-conditioning-cheat-sheet-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdConditioningCheatSheetPageComponent {
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly gateInfo = inject(HdGateInfoService);

  protected readonly referenceConfig = REFERENCE_BODYGRAPH_CONFIG;

  /**
   * Alle negen blokken in Figma-leesvolgorde (boven → onder). Op desktop worden
   * ze absoluut gepositioneerd via de `--<center>`-modifierklassen in de SCSS
   * (1:1 opgemeten op het Figma-poster); op smalle schermen bepaalt deze
   * volgorde de gestapelde lijst.
   */
  protected readonly posterCenters: readonly HdBodygraphCenter[] = [
    'hoofd', 'ajna', 'keel', 'zelf', 'hart', 'sacraal', 'milt', 'solarplexus', 'root',
  ];

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly title = computed(() => this.text('title'));
  protected readonly subtitle = computed(() => this.text('subtitle'));

  protected readonly impactMarkdownUrl = computed(
    () => `assets/docs/human-design/conditioning/${this.lang()}/impact.md`,
  );
  protected readonly terminologyMarkdownUrl = computed(
    () => `assets/docs/human-design/conditioning/${this.lang()}/terminology.md`,
  );

  constructor() {
    // Beide content-talen eager laden (idempotent per taal) zodat de taalwissel
    // én de NL-fallback meteen correcte tekst opleveren.
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');
  }

  protected print(): void {
    window.print();
  }

  /** Conditioning-scope tekst met NL-fallback voor (nog) lege EN-velden. */
  private text(key: string): string {
    const lang = this.lang();
    const value = this.gateInfo.cheatSheetTextIn('conditioning', key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetTextIn('conditioning', key, 'nl');
  }
}
